import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDown, Link2, BarChart3, KeyRound, Zap, Sparkles, Heart, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { startGoogleLogin } from "@/lib/api";
import { FadeIn } from "@/components/common";
import { PartnerLeadForm } from "@/components/PartnerLeadForm";

const LOGO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_goose-platform/artifacts/eydl4x1d_Goose%20Logo2.png";
const LOGO_WHITE = "https://customer-assets-7cd3h4nn.emergentagent.net/job_goose-platform/artifacts/4908656fc5ac556a_Asset%209%404x-8.webp";
const PHONES = "https://customer-assets-7cd3h4nn.emergentagent.net/job_goose-platform/artifacts/deptsjdm_ChatGPT%20Image%20Oct%203%2C%202026%2C%2001_45_01%20PM.png";
const EVENT1 = "https://customer-assets-7cd3h4nn.emergentagent.net/job_goose-platform/artifacts/x51ru1su_attractive-women-socializing-at-a-lively-bar-2026-09-22-17-26-44-utc.webp";
const DATEIMG = "https://customer-assets-7cd3h4nn.emergentagent.net/job_goose-platform/artifacts/9onvc0z3_shutterstock_490194880.webp";
const LAPTOP = "https://customer-assets-7cd3h4nn.emergentagent.net/job_goose-platform/artifacts/379ridcj_GOOSE%20Event%20Dashboard%20Laptop%20Mockup%20%281%29.png";
const MATCH_MONDAY = "https://customer-assets-7cd3h4nn.emergentagent.net/job_goose-platform/artifacts/24hk9dl6_image.png";

/* HAEVN design tokens (see /app/memory/DESIGN_RULES.md) */
const RED = "#C92F2F";
const RED_HOVER = "#A82525";
const CONTAINER = "mx-auto w-full max-w-[1200px] px-6 sm:px-10 lg:px-[72px]";
const CONTAINER_WIDE = "mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-[72px]";
const HAIRLINE = "border-t border-[#EEEEEE]";

/* type presets */
const EYEBROW = "text-[11px] font-medium uppercase tracking-[0.22em] text-[#89909A]";
const H1 = "font-display text-[2rem] sm:text-[2.625rem] font-bold leading-[1.02] tracking-[-0.035em] text-[#111111]";
const PAYOFF = "font-display text-[22px] font-bold leading-[1.25] tracking-[-0.015em] text-[#111111]";
const ITEM_TITLE = "font-display text-[20px] font-bold leading-[1.25] text-[#111111]";
const BODY_TXT = "text-[15px] leading-[1.55] text-[#3F4650]";

const STEPS = [
  { n: "1", title: "They notice someone.", body: "Guests connect naturally\nat your event." },
  { n: "2", title: "They enter their Goose number.", body: "No profiles. No swiping.\nJust the number on their badge." },
  { n: "3", title: "They see how well they align.", body: "Goose gives them an instant\ncompatibility preview." },
];

const FEATURES = [
  { icon: Link2, title: "Easy guest setup", body: "One link gets guests into Goose and connects them to the HAEVN compatibility survey before your event." },
  { icon: BarChart3, title: "See who’s ready", body: "Track how many guests have joined Goose and completed the survey before event night." },
  { icon: KeyRound, title: "Event-night access", body: "Guests verify their email and receive their event-specific Goose number." },
  { icon: Zap, title: "Compatibility on demand", body: "Guests enter another guest’s number and instantly see how compatible they are." },
];

const BUSINESS = [
  { icon: Sparkles, title: "Differentiate your events", body: "Give guests something they can’t get at a standard singles event: real compatibility information about the people they meet." },
  { icon: Heart, title: "Create more guest value", body: "Give people useful information during the event and additional opportunities to meet highly compatible people afterward." },
  { icon: Lock, title: "Keep control", body: "You remain the event producer. Your brand, pricing, audience and customer relationship remain yours. Goose provides the compatibility infrastructure." },
];

const WORKFLOW = [
  { n: "1", title: "Connect Eventbrite", body: "Connect your existing event and bring your registered guests into Goose." },
  { n: "2", title: "Track guest readiness", body: "See who has joined Goose and completed the compatibility survey." },
  { n: "3", title: "Run Goose on event night", body: "Your surveyed guests are ready to use Goose when the event begins." },
];

const Dot = () => (
  <span className="inline-block h-1.5 w-1.5 shrink-0 rounded-full align-middle" style={{ backgroundColor: RED }} />
);

export default function Partners() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      navigate(user.role === "operator" ? "/admin" : "/producer", { replace: true });
    }
  }, [user, loading, navigate]);

  return (
    <div className="min-h-screen bg-white font-[Satoshi,sans-serif] antialiased">
      {/* ===== SECTION 1: HEADER + HERO + THREE STEPS ===== */}
      <header className={`${CONTAINER} flex items-center justify-between py-6`}>
        <button data-testid="goose-logo" onClick={() => navigate("/")} className="shrink-0">
          <img src={LOGO} alt="Goose, powered by HAEVN" className="w-[180px]" />
        </button>
        <button
          data-testid="nav-signin-btn"
          onClick={() => startGoogleLogin()}
          className="rounded-full border border-[#111111] px-6 py-3 text-[14px] font-semibold text-[#111111] transition-colors duration-200 hover:text-[#C92F2F] hover:border-[#C92F2F]"
        >
          Sign in
        </button>
      </header>

      {/* Hero */}
      <section className={`${CONTAINER} pb-16 pt-4`}>
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-10">
          <FadeIn>
            <h1 className="font-display text-[2.375rem] sm:text-[4rem] lg:text-[4.5rem] font-extrabold leading-[1.0] sm:leading-[0.98] tracking-[-0.04em] text-[#111111]">
              Give your guests<br />
              <span style={{ color: RED }}>more</span> than a<br />
              room full of singles.
            </h1>
            <div className={`mt-7 max-w-[640px] space-y-3 ${BODY_TXT}`}>
              <p>Goose adds HAEVN compatibility intelligence to the singles events you already produce.</p>
              <p>Guests meet naturally. When someone catches their attention, Goose shows them how well they align.</p>
            </div>
            <div className="mt-8 text-center lg:text-left">
              <button
                data-testid="hero-howitworks-btn"
                onClick={() => document.getElementById("how-goose-works")?.scrollIntoView({ behavior: "smooth" })}
                className="group inline-flex items-center gap-2 rounded-full px-6 py-3 text-[14px] font-semibold text-white transition-colors duration-200"
                style={{ backgroundColor: RED }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = RED_HOVER)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = RED)}
              >
                See how Goose works
                <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" />
              </button>
            </div>
          </FadeIn>

          <FadeIn delay={0.15}>
            <div className="mx-auto w-full max-w-[600px] min-[1400px]:max-w-none min-[1400px]:w-[690px] min-[1400px]:-translate-x-10">
              <img src={PHONES} alt="Three Goose compatibility previews showing 73%, 84% and 64% alignment" className="h-auto w-full" />
            </div>
          </FadeIn>
        </div>

        {/* Three steps */}
        <div className="mt-20 grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-10">
          {STEPS.map((s) => (
            <FadeIn key={s.n}>
              <div className="text-center lg:text-left">
                <div className="flex items-center justify-center gap-4 lg:justify-start">
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-xl font-bold lg:h-11 lg:w-11 lg:text-[15px]" style={{ backgroundColor: "#FDECED", color: RED }}>
                    {s.n}
                  </span>
                  <span className="hidden h-px flex-1 bg-[#EEEEEE] lg:block" />
                </div>
                <h3 className={`mt-5 ${ITEM_TITLE}`}>{s.title}</h3>
                <p className={`mt-2 whitespace-pre-line ${BODY_TXT}`}>{s.body}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ===== SECTION 2: ATTRACTION VS COMPATIBILITY ===== */}
      <section className={`relative w-full overflow-hidden bg-white ${HAIRLINE}`}>
        {/* Desktop: photo bleeds from the right, fades to white under the copy */}
        <div className="relative hidden lg:block">
          <div className="absolute inset-y-0 right-0 w-[58%]">
            <img src={DATEIMG} alt="A woman on a date weighing whether the connection is worth her time" className="h-full w-full object-cover object-[42%_22%]" />
          </div>
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(to right, #ffffff 0%, #ffffff 40%, rgba(255,255,255,0.92) 48%, rgba(255,255,255,0.5) 58%, rgba(255,255,255,0) 68%)" }}
          />
          <div className={`${CONTAINER} relative py-20`}>
            <FadeIn>
              <div className="max-w-[640px]">
                <h2 className={H1}>
                  Attraction gets your attention. But it doesn’t tell you if you’re <span style={{ color: RED }}>compatible</span>.
                </h2>
                <div className="mt-6 h-1 w-16 rounded-full" style={{ backgroundColor: RED }} />
                <p className={`mt-6 max-w-[640px] ${BODY_TXT}`}>
                  Dating already asks a lot of people: time, energy, money, and emotional investment. Goose helps your guests understand whether there’s real compatibility before they decide how much more of themselves to invest.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>

        {/* Mobile: copy then full photo */}
        <div className="lg:hidden">
          <div className={`${CONTAINER} pb-10 pt-12`}>
            <FadeIn>
              <h2 className={H1}>
                Attraction gets your attention. But it doesn’t tell you if you’re <span style={{ color: RED }}>compatible</span>.
              </h2>
              <div className="mt-6 h-1 w-16 rounded-full" style={{ backgroundColor: RED }} />
              <p className={`mt-6 ${BODY_TXT}`}>
                Dating already asks a lot of people: time, energy, money, and emotional investment. Goose helps your guests understand whether there’s real compatibility before they decide how much more of themselves to invest.
              </p>
            </FadeIn>
          </div>
          <div className="relative">
            <img src={DATEIMG} alt="A woman on a date weighing whether the connection is worth her time" className="h-[360px] w-full object-cover object-[25%_20%]" />
            <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-white to-transparent" />
          </div>
        </div>
      </section>

      {/* ===== SECTION 3: EVENT PHOTOGRAPH (dark hero) ===== */}
      <section className="w-full bg-black">
        <div className="relative w-full overflow-hidden py-16 lg:h-[520px] lg:py-0">
          <div
            className="absolute inset-0 hidden bg-no-repeat lg:block"
            style={{ backgroundImage: `url(${EVENT1})`, backgroundSize: "155% auto", backgroundPosition: "80% 56%" }}
          />
          <div
            className="absolute inset-0 hidden lg:block"
            style={{ background: "linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0.3) 45%, rgba(0,0,0,0.65) 58%, rgba(0,0,0,0.9) 70%, rgba(0,0,0,1) 85%, rgba(0,0,0,1) 100%)" }}
          />
          <div className="relative lg:absolute lg:inset-0">
            <div className={`${CONTAINER_WIDE} flex h-full items-center justify-center lg:justify-end`}>
              <FadeIn>
                <div className="max-w-[600px] text-center text-white lg:text-left">
                  <h2 className="font-display text-[2rem] sm:text-[2.625rem] font-extrabold leading-[1.05] tracking-[-0.03em]">
                    You create the room.<br />
                    <span style={{ color: RED }}>Goose</span> adds the intelligence.
                  </h2>
                  <p className="mt-5 text-[15px] leading-[1.55] text-white/90">
                    Your event. Your brand. Your audience. Your pricing. Your customer relationship.
                  </p>
                  <div className="mx-auto mt-5 h-1 w-16 rounded-full lg:mx-0" style={{ backgroundColor: RED }} />
                  <p className="mt-5 text-[15px] leading-[1.55] text-white/80">
                    We provide the compatibility infrastructure to make the experience you already create more valuable.
                  </p>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SECTION 5: HOW GOOSE WORKS ===== */}
      <section id="how-goose-works" className={`${CONTAINER} scroll-mt-24 py-20 ${HAIRLINE}`}>
        <FadeIn>
          <h2 className={`${H1} text-center lg:text-left`}>How Goose works for your event</h2>
        </FadeIn>
        <div className="mt-12 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map((f) => (
            <FadeIn key={f.title}>
              <div className="text-center lg:text-left">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full lg:mx-0" style={{ backgroundColor: "#FDECED", color: RED }}>
                  <f.icon className="h-6 w-6" />
                </div>
                <h3 className={`mt-5 ${ITEM_TITLE}`}>{f.title}</h3>
                <p className={`mt-2.5 ${BODY_TXT}`}>{f.body}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ===== SECTION 5.5: FITS THE WAY YOU ALREADY WORK ===== */}
      <section className={`w-full bg-[#F6F7F8] ${HAIRLINE}`}>
        <div className={`${CONTAINER} py-20`}>
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
            <FadeIn>
              <div className="mx-auto max-w-[560px] text-center lg:mx-0 lg:text-left">
                <p className={EYEBROW}>Fits the way you already work</p>
                <h2 className={`mt-4 ${H1}`}>
                  Connect your event.<br />
                  Goose handles the <span style={{ color: RED }}>intelligence</span>.
                </h2>
                <p className={`mt-6 ${BODY_TXT}`}>
                  Connect your Eventbrite event to Goose and keep your guest list in sync. From one dashboard, see who has joined Goose, who has completed the HAEVN compatibility survey, and who is ready for event night.
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.15}>
              <img
                data-testid="eventbrite-laptop-image"
                src={LAPTOP}
                alt="Goose event dashboard on a laptop showing registered guests, survey completion and event-night readiness"
                className="h-auto w-full"
              />
            </FadeIn>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-12 sm:grid-cols-3 sm:gap-12">
            {WORKFLOW.map((w) => (
              <FadeIn key={w.n}>
                <div data-testid={`eventbrite-workflow-${w.n}`} className="text-center lg:text-left">
                  <span className="block font-display text-[44px] font-extrabold leading-none sm:text-[20px] sm:leading-normal" style={{ color: RED }}>{w.n}</span>
                  <h3 className={`mt-3 sm:mt-2 ${ITEM_TITLE}`}>{w.title}</h3>
                  <p className={`mt-2 ${BODY_TXT}`}>{w.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 7: MATCH MONDAY ===== */}
      <section className={`w-full bg-white ${HAIRLINE}`}>
        {/* --- Desktop composition (approved mockup) --- */}
        <div className="relative hidden overflow-hidden lg:block">
          <div className={`relative z-10 ${CONTAINER}`}>
            <div className="grid grid-cols-2 gap-16">
              <FadeIn>
                <div className="py-28">
                  <p className={EYEBROW}>The event doesn’t have to be the end</p>
                  <h2 className={`mt-4 max-w-[560px] ${H1}`}>
                    Give your guests more than one night to find the right person.
                  </h2>
                  <p className={`mt-6 max-w-[540px] ${BODY_TXT}`}>
                    <span className="font-bold text-[#111111]">Match Monday</span> extends the value of your event by continuing to introduce guests to highly compatible people after it’s over.
                  </p>
                  <div className="mt-7 h-1 w-16 rounded-full" style={{ backgroundColor: RED }} />
                  <p className={`mt-7 max-w-[460px] ${BODY_TXT}`}>
                    Your event creates the room. Goose helps them make the most of it.
                  </p>
                  <p className="mt-1 max-w-[460px] font-display text-[15px] font-bold leading-[1.5] text-[#111111]">
                    Match Monday keeps the opportunity going.
                  </p>
                  <div className="mt-10 max-w-[520px] border-l-2 pl-5" style={{ borderColor: RED }}>
                    <p className="text-[12px] font-bold uppercase tracking-[0.18em] text-[#111111]">The value stays associated with your event.</p>
                    <p className={`mt-3 ${BODY_TXT}`}>
                      Your guests discover Match Monday through the experience you created. HAEVN powers the matching, but you gave them access to something that keeps delivering value after event night.
                    </p>
                  </div>
                </div>
              </FadeIn>
              <div aria-hidden />
            </div>
          </div>
          <img
            data-testid="match-monday-image"
            src={MATCH_MONDAY}
            alt="Match Monday calendar showing several Mondays, each with multiple compatible matches"
            className="pointer-events-none absolute inset-y-0 right-0 z-0 h-full w-[78%] object-contain object-right"
          />
        </div>

        {/* --- Mobile composition --- */}
        <div className="lg:hidden">
          <div className={`${CONTAINER} py-20`}>
            <div className="grid items-center gap-6">
              <FadeIn>
                <div className="text-center">
                  <p className={EYEBROW}>The event doesn’t have to be the end</p>
                  <h2 className={`mx-auto mt-4 max-w-[560px] ${H1}`}>
                    Give your guests more than one night to find the right person.
                  </h2>
                  <p className={`mx-auto mt-6 max-w-[540px] ${BODY_TXT}`}>
                    <span className="font-bold text-[#111111]">Match Monday</span> extends the value of your event by continuing to introduce guests to highly compatible people after it’s over.
                  </p>
                  <div className="mx-auto mt-7 h-1 w-16 rounded-full" style={{ backgroundColor: RED }} />
                  <p className={`mx-auto mt-7 max-w-[460px] ${BODY_TXT}`}>
                    Your event creates the room. Goose helps them make the most of it.
                  </p>
                  <p className="mx-auto mt-1 max-w-[460px] font-display text-[15px] font-bold leading-[1.5] text-[#111111]">
                    Match Monday keeps the opportunity going.
                  </p>
                </div>
              </FadeIn>
              <FadeIn delay={0.15}>
                <div className="-mx-6 aspect-[941/1380] overflow-hidden sm:-mx-10">
                  <img
                    src="https://customer-assets-7cd3h4nn.emergentagent.net/job_goose-platform/artifacts/t6vxbo53_image.png"
                    alt="Match Monday calendar showing several Mondays, each with multiple compatible matches"
                    className="h-full w-full object-cover object-[50%_41%]"
                  />
                </div>
              </FadeIn>
            </div>

            <FadeIn>
              <div className="mx-auto mt-6 max-w-[560px] text-center">
                <p className="font-display text-[20px] font-bold text-[#111111]">The value stays associated with your event.</p>
                <p className={`mt-2 ${BODY_TXT}`}>
                  Your guests discover Match Monday through the experience you created. HAEVN powers the matching, but you gave them access to something that keeps delivering value after event night.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ===== SECTION 8: THE BUSINESS CASE ===== */}
      <section className={`w-full bg-[#F6F7F8] ${HAIRLINE}`}>
        <div className={`${CONTAINER} py-20`}>
          <FadeIn>
            <h2 className={`mx-auto max-w-[860px] text-center lg:mx-0 lg:text-left ${H1}`}>
              Make the events you already produce more valuable.
            </h2>
            <p className={`mx-auto mt-6 max-w-[960px] text-center lg:mx-0 lg:text-left ${PAYOFF}`}>
              Your event. Your brand. Your audience. Your pricing. Your customer relationship.
            </p>
            <p className={`mx-auto mt-5 max-w-[640px] text-center lg:mx-0 lg:text-left ${BODY_TXT}`}>
              Goose doesn’t replace what you’ve built. It adds compatibility intelligence and an ongoing matching experience to it.
            </p>
          </FadeIn>
          <div className="mt-14 grid grid-cols-1 gap-12 md:grid-cols-3 md:gap-10">
            {BUSINESS.map((b) => (
              <FadeIn key={b.title}>
                <div className="text-center lg:text-left">
                  <b.icon className="mx-auto hidden h-7 w-7 lg:mx-0 lg:block" strokeWidth={1.75} style={{ color: RED }} />
                  <div className="mx-auto mb-5 h-0.5 w-10 rounded-full lg:hidden" style={{ backgroundColor: RED }} />
                  <h3 className={`mt-5 ${ITEM_TITLE}`}>{b.title}</h3>
                  <p className={`mt-3 ${BODY_TXT}`}>{b.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECTION 9: THE ECONOMICS ===== */}
      <section className={`w-full bg-white ${HAIRLINE}`}>
        <div className={`${CONTAINER} py-20`}>
          <div className="grid items-center gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-12">
            <FadeIn>
              <p className={EYEBROW}>Simple partner pricing</p>
              <p className="mt-2 font-display font-extrabold leading-[0.9] tracking-[-0.04em] text-[clamp(72px,9vw,116px)]" style={{ color: RED }}>$10</p>
              <h2 className="mt-1 font-display text-[1.625rem] sm:text-[1.875rem] font-bold leading-[1.1] tracking-[-0.02em] text-[#111111]">
                per Goose-enabled guest.
              </h2>
              <h3 className="mt-4 font-display text-[1.375rem] sm:text-[1.625rem] font-bold leading-[1.15] tracking-[-0.02em] text-[#111111]">
                Goose doesn’t have to reduce your margin.
              </h3>
              <p className={`mt-3 max-w-[560px] ${BODY_TXT}`}>
                Add the $10 Goose fee to your ticket price, pass through exactly what you pay, or build in additional margin. <span className="font-bold text-[#111111]">Your ticket price is entirely up to you.</span>
              </p>
              <div className="mt-5 h-1 w-16 rounded-full" style={{ backgroundColor: RED }} />
              <p className="mt-5 max-w-[560px] font-display text-[1.25rem] sm:text-[1.375rem] font-bold leading-[1.25] tracking-[-0.015em] text-[#111111]">
                You set the price. You keep the revenue. We provide Goose.
              </p>
              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2.5">
                {["No franchise fee.", "No revenue share.", "No long-term licensing commitment."].map((s) => (
                  <span key={s} className={`flex items-center gap-2 ${BODY_TXT}`}>
                    <Dot /> {s}
                  </span>
                ))}
              </div>
              <p className="mt-4 max-w-[520px] text-[13px] leading-[1.5] text-[#89909A]">
                You’re billed only for guests who complete the HAEVN compatibility survey by the event cutoff.
              </p>
            </FadeIn>
            <FadeIn delay={0.15}>
              <img
                data-testid="pricing-ticket-image"
                src="https://customer-assets-7cd3h4nn.emergentagent.net/job_goose-platform/artifacts/halpxxak_image.png"
                alt="Example event ticket: a $40 ticket plus a $10 Goose fee, so the guest pays $50, with a note that you could charge $55 or $60 instead"
                className="h-auto w-full"
              />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ===== SECTION 10: FINAL CONVERSION — Event Partner lead form ===== */}
      <section id="apply" className={`w-full scroll-mt-24 bg-[#F6F7F8] ${HAIRLINE}`}>
        <div className={`${CONTAINER_WIDE} py-20 lg:py-24`}>
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <FadeIn>
              <div>
                <h2 className="font-display text-[3rem] font-extrabold leading-[0.98] tracking-[-0.04em] text-[#111111] sm:text-[3.75rem] lg:text-[4.25rem]">
                  Ready to bring <span style={{ color: RED }}>Goose</span> to your events?
                </h2>
                <p className="mt-6 max-w-[520px] text-[17px] leading-[1.5] text-[#3F4650] sm:text-[18px]">
                  Tell us a little about the events you produce. We’ll show you how Goose can fit into the events you already run.
                </p>
                <div className="mt-8 h-1 w-16 rounded-full" style={{ backgroundColor: RED }} />
                <p className="mt-8 text-[1.25rem] font-bold text-[#111111]">No commitment. No sales gauntlet.</p>
                <p className="mt-2 max-w-[480px] text-[17px] leading-[1.5] text-[#3F4650] sm:text-[18px]">
                  Just a conversation about your events and whether Goose makes sense.
                </p>
                <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#3F4650] lg:flex-nowrap lg:whitespace-nowrap">
                  <span>Your event.</span>
                  <span className="hidden h-px w-6 shrink-0 sm:inline-block" style={{ backgroundColor: RED }} />
                  <span>Your brand.</span>
                  <span className="hidden h-px w-6 shrink-0 sm:inline-block" style={{ backgroundColor: RED }} />
                  <span>Your customer relationship.</span>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.1}>
              <PartnerLeadForm />
            </FadeIn>
          </div>
        </div>
      </section>

      <footer className="bg-[#0A0E1A]">
        <div className={`${CONTAINER} flex items-center justify-between py-8`}>
          <img src={LOGO_WHITE} alt="HAEVN" className="w-[132px]" />
          <span className="text-[12px] font-medium tracking-[0.01em] text-white/50">Goose V1 · powered by HAEVN</span>
        </div>
      </footer>
    </div>
  );
}
